import actions from "src/actions";
import type { FacebookPage, InstagramAccount } from "../types";
import {
  mapPartnershipPartnerOptionsResponse,
  mergeAdvertiserFacebookPagesForPicker,
  mergeAdvertiserInstagramAccountsForPicker,
  mergePartnerFacebookPagesForPicker,
  type PartnershipIdentityCatalog,
  type PartnershipPartnerOptionsApiResponse,
} from "./mapPartnershipPartnerOptionsResponse";
import type {
  CatalogRefreshScheduleOutcome,
  MetaIdentityCatalogSyncSummary,
} from "./metaIdentityCatalogApi";
import {
  parseListInstagramApiResponse,
  parseListPagesApiResponse,
  shouldPollIdentityCatalogAfterRead,
  isCatalogRefreshInFlight,
  enrichIdentityCatalogPictures,
  type ListPagesApiRow,
} from "./metaIdentityCatalogApi";
import { metaGraphPagePictureUrl } from "@common/utils/metaGraphPagePictureUrl";
import { pollCatalogRowsAfterRefresh } from "./pollCatalogAfterRefresh";

export type { ListPagesApiRow };

function catalogPollMaxWaitMs(
  priorCount: number,
  catalogSync?: MetaIdentityCatalogSyncSummary
): number | undefined {
  const count = Math.max(priorCount, catalogSync?.page_count ?? 0);
  return count > 50 ? 120_000 : undefined;
}

const catalogSyncPollOptions = {
  initialDelayMs: 5_000,
  backoffFactor: 1.5,
  stablePollIntervalMs: 8_000,
} as const;

function pageRowIsAdAccountPage(
  page: ListPagesApiRow
): boolean {
  const sources = page.page_sources;
  if (!sources?.length) {
    return true;
  }
  return sources.includes("promote_pages");
}

/** GET /ads/pages — promote_pages ∪ /me/accounts for advertiser identity pickers. */
export function mapAdvertiserIdentityPagesFromListPages(
  rows: readonly ListPagesApiRow[] | null | undefined
): FacebookPage[] {
  return (rows ?? []).map((page) => {
    const linkedId =
      page.linked_instagram_user_id?.trim() ||
      page.instagram_business_account?.id?.trim() ||
      undefined;
    const linkedUsername =
      page.linked_instagram_username?.trim() ||
      page.instagram_business_account?.username?.trim() ||
      undefined;
    return {
      id: page.id,
      name: page.name,
      username: page.username,
      picture: metaGraphPagePictureUrl(page.picture) || "",
      is_ad_account_page: pageRowIsAdAccountPage(page),
      is_eligible_for_branded_content: page.is_eligible_for_branded_content,
      ...(linkedId ? { linked_instagram_user_id: linkedId } : {}),
      ...(linkedUsername ? { linked_instagram_username: linkedUsername } : {}),
    };
  });
}

type IdentityFetchOptions = {
  ad_account_id: string;
  force_sync?: boolean;
  signal?: AbortSignal;
  skipSchedule?: boolean;
  awaitCombinedCatalogComplete?: boolean;
};

async function fetchAdvertiserIdentityPagesInternal(
  args: IdentityFetchOptions
): Promise<{
  pages: FacebookPage[];
  catalog_sync?: MetaIdentityCatalogSyncSummary;
  refreshOutcome?: CatalogRefreshScheduleOutcome;
}> {
  const adAccountId = args.ad_account_id.trim();
  const fetchRows = async () => {
    const response = await actions.ADMIN.listPages({
      ad_account_id: adAccountId,
    });
    const parsed = parseListPagesApiResponse(response);
    return { rows: parsed.pages, catalog_sync: parsed.catalog_sync };
  };

  let initial = await fetchRows();
  if (args.signal?.aborted) {
    return {
      pages: mapAdvertiserIdentityPagesFromListPages(initial.rows),
      catalog_sync: initial.catalog_sync,
      refreshOutcome: initial.catalog_sync?.refresh_outcome,
    };
  }

  let rows = initial.rows;
  let refreshOutcome = initial.catalog_sync?.refresh_outcome;
  if (args.force_sync) {
    const forced = await actions.ADMIN.listPages({
      ad_account_id: adAccountId,
      force_sync: true,
    });
    const forcedParsed = parseListPagesApiResponse(forced);
    refreshOutcome =
      forcedParsed.catalog_sync?.refresh_outcome ?? refreshOutcome;
    rows = await pollCatalogRowsAfterRefresh({
      fetchRows,
      priorCount: rows.length,
      priorCatalogSync: initial.catalog_sync,
      forceSync: true,
      requireCatalogComplete: true,
      signal: args.signal,
      maxWaitMs: catalogPollMaxWaitMs(rows.length, initial.catalog_sync),
      ...catalogSyncPollOptions,
    });
  } else if (
    shouldPollIdentityCatalogAfterRead({
      rowCount: rows.length,
      catalog_sync: initial.catalog_sync,
      awaitCombinedCatalogComplete: args.awaitCombinedCatalogComplete,
    })
  ) {
    rows = await pollCatalogRowsAfterRefresh({
      fetchRows,
      priorCount: rows.length,
      priorCatalogSync: initial.catalog_sync,
      requireCatalogComplete: true,
      signal: args.signal,
      maxWaitMs: catalogPollMaxWaitMs(rows.length, initial.catalog_sync),
      ...catalogSyncPollOptions,
    });
  }

  const final = await fetchRows().catch(() => initial);
  const outcomeRows =
    final.rows.length > rows.length ? final.rows : rows;
  return {
    pages: mapAdvertiserIdentityPagesFromListPages(outcomeRows),
    catalog_sync: final.catalog_sync ?? initial.catalog_sync,
    refreshOutcome:
      refreshOutcome ??
      final.catalog_sync?.refresh_outcome ??
      initial.catalog_sync?.refresh_outcome,
  };
}

async function fetchAdvertiserIdentityInstagramAccountsInternal(
  args: IdentityFetchOptions
): Promise<InstagramAccount[]> {
  const adAccountId = args.ad_account_id.trim();
  const fetchRows = async () => {
    const response = await actions.ADMIN.listInstagramAccounts({
      ad_account_id: adAccountId,
    });
    const parsed = parseListInstagramApiResponse(response);
    return {
      rows: parsed.instagram_accounts as InstagramAccount[],
      catalog_sync: parsed.catalog_sync,
    };
  };

  let initial = await fetchRows();
  if (args.signal?.aborted) {
    return initial.rows;
  }

  let rows = initial.rows;
  if (
    shouldPollIdentityCatalogAfterRead({
      rowCount: rows.length,
      catalog_sync: initial.catalog_sync,
      forceSync: Boolean(args.force_sync),
      awaitCombinedCatalogComplete: args.awaitCombinedCatalogComplete,
    })
  ) {
    rows = await pollCatalogRowsAfterRefresh({
      fetchRows,
      priorCount: rows.length,
      priorCatalogSync: initial.catalog_sync,
      forceSync: Boolean(args.force_sync),
      requireCatalogComplete: true,
      signal: args.signal,
      maxWaitMs: catalogPollMaxWaitMs(0, initial.catalog_sync),
      ...catalogSyncPollOptions,
    });
  }

  return rows;
}

export async function fetchAdvertiserIdentityPages(args: {
  ad_account_id: string;
  force_sync?: boolean;
  signal?: AbortSignal;
}): Promise<FacebookPage[]> {
  const result = await fetchAdvertiserIdentityPagesInternal(args);
  return result.pages;
}

/** GET /ads/instagram_accounts — full ad-account IG catalog for advertiser identity pickers. */
export async function fetchAdvertiserIdentityInstagramAccounts(args: {
  ad_account_id: string;
  force_sync?: boolean;
  signal?: AbortSignal;
  skipSchedule?: boolean;
}): Promise<InstagramAccount[]> {
  return fetchAdvertiserIdentityInstagramAccountsInternal(args);
}

/** Advertiser Facebook pages + Instagram catalog for identity pickers. */
export async function fetchAdvertiserIdentityData(args: {
  ad_account_id: string;
  force_sync?: boolean;
  signal?: AbortSignal;
}): Promise<{
  pages: FacebookPage[];
  instagramAccounts: InstagramAccount[];
  refreshOutcome?: CatalogRefreshScheduleOutcome;
}> {
  const pagesResult = await fetchAdvertiserIdentityPagesInternal({
    ...args,
    skipSchedule: false,
  });
  if (args.signal?.aborted) {
    return {
      pages: pagesResult.pages,
      instagramAccounts: [],
      refreshOutcome: pagesResult.refreshOutcome,
    };
  }
  const instagramAccounts = await fetchAdvertiserIdentityInstagramAccountsInternal(
    {
      ...args,
      force_sync: false,
      skipSchedule: true,
      awaitCombinedCatalogComplete:
        pagesResult.catalog_sync?.catalog_complete !== true &&
        isCatalogRefreshInFlight(pagesResult.refreshOutcome),
    }
  );
  const enriched = enrichIdentityCatalogPictures(
    pagesResult.pages,
    instagramAccounts
  );
  return {
    pages: enriched.pages,
    instagramAccounts: enriched.instagramAccounts,
    refreshOutcome: pagesResult.refreshOutcome,
  };
}

export type PartnershipPartnerOptionsLoad = PartnershipIdentityCatalog;

/**
 * Full partnership_partner_options fetch. Used for the initial advertiser catalog load
 * and for permission-aware refetches while partner post is enabled — not on every
 * advertiser field edit when partner post is off.
 */

/** Splits one API catalog into the state fields screens pass to creative editors. */
export function partnershipIdentityCatalogToEditorState(
  catalog: PartnershipIdentityCatalog
): {
  advertiserPages: PartnershipIdentityCatalog["advertiserFacebookPages"];
  partnerPages: PartnershipIdentityCatalog["partnerFacebookPages"];
  instagramAccounts: PartnershipIdentityCatalog["instagramAccounts"];
  partnerInstagramAccounts: PartnershipIdentityCatalog["partnerInstagramAccounts"];
  advertiserValidationMessages: string[];
  partnershipPermissionsLoaded: boolean;
  metaBusinessId?: string;
} {
  return {
    advertiserPages: catalog.advertiserFacebookPages,
    partnerPages: catalog.partnerFacebookPages,
    instagramAccounts: catalog.instagramAccounts,
    partnerInstagramAccounts: catalog.partnerInstagramAccounts,
    advertiserValidationMessages: catalog.advertiserValidationMessages,
    partnershipPermissionsLoaded: catalog.partnershipPermissionsLoaded,
    metaBusinessId: catalog.metaBusinessId,
  };
}

export function partnershipCatalogEditorSliceForKey(
  catalogByKey: Readonly<Record<string, PartnershipIdentityCatalog>>,
  fetchKey: string
): ReturnType<typeof partnershipIdentityCatalogToEditorState> | undefined {
  const catalog = catalogByKey[fetchKey];
  if (!catalog) {
    return undefined;
  }
  return partnershipIdentityCatalogToEditorState(catalog);
}

export async function fetchPartnershipPartnerOptions(args: {
  ad_account_id: string;
  advertiser_page_id?: string;
  advertiser_instagram_user_id?: string;
  eligible_only?: boolean;
  instagram_accounts_only?: boolean;
  signal?: AbortSignal;
}): Promise<PartnershipIdentityCatalog> {
  const adAccountId = args.ad_account_id.trim();
  if (args.instagram_accounts_only) {
    const data = await actions.ADMIN.listPartnershipPartnerOptions({
      ad_account_id: adAccountId,
      advertiser_page_id: args.advertiser_page_id?.trim() || undefined,
      advertiser_instagram_user_id:
        args.advertiser_instagram_user_id?.trim() || undefined,
      eligible_only: args.eligible_only,
      instagram_accounts_only: true,
    });
    return mapPartnershipPartnerOptionsResponse(
      data as PartnershipPartnerOptionsApiResponse
    );
  }

  const partnershipFetch = actions.ADMIN.listPartnershipPartnerOptions({
    ad_account_id: adAccountId,
    advertiser_page_id: args.advertiser_page_id?.trim() || undefined,
    advertiser_instagram_user_id:
      args.advertiser_instagram_user_id?.trim() || undefined,
    eligible_only: args.eligible_only,
    instagram_accounts_only: args.instagram_accounts_only,
  });

  const data = await partnershipFetch;
  const catalog = mapPartnershipPartnerOptionsResponse(
    data as PartnershipPartnerOptionsApiResponse
  );

  if (
    catalog.advertiserFacebookPages.length > 0 ||
    catalog.instagramAccounts.length > 0
  ) {
    return catalog;
  }

  const identity = await fetchAdvertiserIdentityData({
    ad_account_id: adAccountId,
    signal: args.signal,
  });

  const advertiserFacebookPages =
    identity.pages.length > 0
      ? mergeAdvertiserFacebookPagesForPicker(
          identity.pages,
          catalog.advertiserFacebookPages
        )
      : catalog.advertiserFacebookPages;
  const instagramAccounts =
    identity.instagramAccounts.length > 0
      ? mergeAdvertiserInstagramAccountsForPicker(
          identity.instagramAccounts,
          catalog.instagramAccounts
        )
      : catalog.instagramAccounts;

  return {
    ...catalog,
    advertiserFacebookPages,
    instagramAccounts,
    partnerFacebookPages: mergePartnerFacebookPagesForPicker(
      advertiserFacebookPages,
      catalog.partnerFacebookPages
    ),
  };
}
